import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

// MIX: Testi di A + Grafica di C
const WelcomePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    const bannerDismissed = localStorage.getItem('install_banner_dismissed');
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile && !isStandalone && !bannerDismissed) {
      setTimeout(() => setShowInstallBanner(true), 2000);
    }
  }, []);

  const dismissBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('install_banner_dismissed', 'true');
  };

  // Testi semplici e diretti (Opzione A)
  const content = {
    it: {
      headline: 'Il tuo smartphone è sicuro?',
      subheadline: 'Scoprilo in pochi minuti con la nostra valutazione professionale',
      cta: 'Inizia la Valutazione',
      footer: 'Analisi gratuita • Risultati immediati • Assistenza professionale'
    },
    en: {
      headline: 'Is your smartphone secure?',
      subheadline: 'Find out in minutes with our professional assessment',
      cta: 'Start Assessment',
      footer: 'Free analysis • Immediate results • Professional support'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-black">
      {/* Animated cyber grid background (Grafica C) */}
      <div className="absolute inset-0">
        {/* Moving grid */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,242,234,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,242,234,0.4) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            animation: 'gridMove 20s linear infinite'
          }}
        />
        
        {/* Radial glow */}
        <div className="absolute inset-0" 
             style={{ background: 'radial-gradient(ellipse at center, rgba(0,242,234,0.15) 0%, transparent 70%)' }} 
        />
        
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-primary/50" />
        <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-primary/50" />
        <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-primary/50" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-primary/50" />
        
        {/* Scanning line effect */}
        <div 
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-60"
          style={{ animation: 'scanLine 4s ease-in-out infinite' }}
        />
      </div>
      
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes scanLine {
          0%, 100% { top: 0%; opacity: 0; }
          50% { opacity: 0.6; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(0,242,234,0.3), inset 0 0 20px rgba(0,242,234,0.05); }
          50% { box-shadow: 0 0 40px rgba(0,242,234,0.5), inset 0 0 30px rgba(0,242,234,0.1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
      
      <LanguageSwitcher />
      
      {/* Install Banner */}
      {showInstallBanner && (
        <div className="fixed top-20 left-6 right-6 z-40 max-w-md mx-auto animate-in slide-in-from-top duration-500">
          <div className="bg-black/90 backdrop-blur-sm rounded-sm p-4 border border-primary/50 shadow-lg shadow-primary/20">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-primary flex-shrink-0 animate-bounce" />
              <div className="flex-1">
                <p className="font-medium text-sm mb-2">
                  {language === 'it' ? 'Installa l\'app' : 'Install the app'}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => navigate('/install')} 
                    className="h-8 px-4 text-xs bg-primary/20 border border-primary text-primary hover:bg-primary hover:text-black">
                    {language === 'it' ? 'Installa' : 'Install'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={dismissBanner} className="h-8 px-3 text-xs">
                    {language === 'it' ? 'Dopo' : 'Later'}
                  </Button>
                </div>
              </div>
              <button onClick={dismissBanner} className="text-muted-foreground hover:text-primary transition">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="relative z-10 max-w-3xl w-full">
        {/* Logo con effetto float */}
        <div className="text-center mb-8" style={{ animation: 'float 6s ease-in-out infinite' }}>
          <img 
            src="/logo.png" 
            alt="Smart Info" 
            className="h-56 md:h-72 lg:h-80 mx-auto"
            data-testid="company-logo"
          />
        </div>

        {/* Main card con effetto neon (Grafica C) */}
        <div 
          className="relative bg-black/80 backdrop-blur-md rounded-sm p-10 md:p-14 border-2 border-primary/70"
          style={{ animation: 'pulse 3s ease-in-out infinite' }}
        >
          {/* Decorative corners */}
          <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-primary" />
          <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-primary" />
          <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-primary" />
          <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-primary" />
          
          {/* Content - Testi semplici (Opzione A) */}
          <div className="text-center">
            {/* Headline grande e diretta */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight leading-tight">
              {t.headline}
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-lg mx-auto leading-relaxed">
              {t.subheadline}
            </p>

            {/* CTA Button con stile neon */}
            <Button
              onClick={() => navigate('/privacy-consent')}
              data-testid="start-assessment-button"
              className="h-16 px-12 rounded-sm bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-black transition-all duration-300 text-lg font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/40 hover:scale-105 transform"
            >
              {t.cta}
              <ArrowRight className="w-5 h-5 ml-3" />
            </Button>
          </div>
        </div>

        {/* Footer con i 3 punti chiave */}
        <p className="text-center text-sm text-muted-foreground/70 mt-10">
          {t.footer}
        </p>
        
        <p className="text-center text-xs text-muted-foreground/50 mt-4">
          Smart Info S.r.l. - Cyber Security Services
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
